import {CommonDecisionTreeNodeType, MacroDecisionTreeNodeType} from '@frontapp/workflows/decision_trees';
import {Edge} from 'reactflow';

import {ErrorWithData} from '../../../../libs/shared/core/src/helpers/types/customErrors';
import {
  DecisionTreeEdge,
  DecisionTreeNode,
} from '../../../../libs/shared/core/src/helpers/types/decisionTreeTypes';
import {
  MacroFlowBuilderNodeData,
  MacroFlowBuilderValidationError,
  MacroFlowBuilderValidationErrorsEnum,
} from '../../../../libs/shared/core/src/helpers/types/macroFlowBuilderTypes';
import {
  BranchNodeDataMacro,
  isBranchNodeMacro,
} from '../../../../libs/shared/core/src/models/api/decisionTrees/decisionTreeNodeDataMacroModel';
import {
  gatherLeafNodes,
  validateTreeData,
} from '../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/helpers';
import {TreeData} from '../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/types';
import {validateFlowBuilderBranchNode} from '../../../../libs/shared/ui/src/components/decisionTrees/flowBuilder/validators';
import {branchByConditionMinOptions, maxMacroFlowNodesCount} from './constants';
import {MacroFlowBuilderStatementValidator} from './hooks/useMacroFlowBuilderStatementValidator';
import {MacroFlowBuilderValidation} from './types';

/*
 * Validators.
 */

/**
 * Validates that the given tree data meets the conditions of our base decision tree, and then performs additional
 * checks specific to trees which represent a Macro flow.
 */
export function validateMacroFlowBuilderTreeData(treeData: TreeData<MacroFlowBuilderNodeData>) {
  const rootNode = validateTreeData(treeData);

  // There must be more than a root node + at least one connection in the flow.
  if (treeData.nodes.length < 2 || treeData.edges.length < 1) {
    throw new Error('Must have a minimum of 2 nodes and 1 edge for a macro flow');
  }

  // Cannot have more than the allowed maximum number of nodes. This may eventually vary by plan.
  if (treeData.nodes.length > maxMacroFlowNodesCount) {
    throw new Error(`Cannot have more than ${maxMacroFlowNodesCount} nodes in a macro flow`);
  }

  if (rootNode.data.type !== MacroDecisionTreeNodeType.START) {
    throw new ErrorWithData('Root macro flow node must have data of type "start"', {
      rootNode: {id: rootNode.id, type: rootNode.type},
    });
  }

  const leafNodes = gatherLeafNodes(treeData.nodes, treeData.edges);
  for (const leafNode of leafNodes) {
    if (leafNode?.data.type !== CommonDecisionTreeNodeType.ACTION) {
      throw new Error('Leaf node found with a type other than "action"');
    }
  }

  const branchNodes = treeData.nodes.filter(isBranchNodeMacro);
  for (const node of branchNodes) {
    validateMacroFlowBuilderBranchNode(treeData.edges, node);
  }
}

/**
 * Handles the standard validation for a Flow Builder branching node and also performs checks that are
 * specific to Macro flow node types such as BRANCH_BY_CONDITION.
 */
function validateMacroFlowBuilderBranchNode(
  edges: ReadonlyArray<Edge>,
  node: DecisionTreeNode<BranchNodeDataMacro>,
) {
  validateFlowBuilderBranchNode(edges, node);

  // `defaultSourceHandle` checks apply to all Macro branching nodes.

  const sourceHandleEdgeMap: Record<string, DecisionTreeEdge> = Object.fromEntries(
    edges.map((edge) => [edge.sourceHandle, edge]),
  );

  const matchingOption = node.data.options.find(
    (option) => option.sourceHandle === node.data.defaultSourceHandle,
  );
  if (matchingOption) {
    throw new ErrorWithData(
      'defaultSourceHandle for branch node should not be the same as any option sourceHandle',
      {
        defaultSourceHandle: node.data.defaultSourceHandle,
        optionSourceHandle: matchingOption.sourceHandle,
      },
    );
  }

  const matchingEdge = sourceHandleEdgeMap[node.data.defaultSourceHandle];
  if (!matchingEdge) {
    throw new ErrorWithData('defaultSourceHandle for branch node has no matching edge', {
      defaultSourceHandle: node.data.defaultSourceHandle,
    });
  }

  // Node type specific checks.

  if (node.data.type === CommonDecisionTreeNodeType.BRANCH_BY_CONDITION) {
    if (node.data.options.length < branchByConditionMinOptions) {
      throw new ErrorWithData('Branch by condition node should have at least one option', {
        nodeId: node.id,
        options: node.data.options,
      });
    }
  }
}

/**
 * Validator that can apply different tests depending on the type of node data being checked.
 */
export function validateNodeData(
  data: MacroFlowBuilderNodeData,
  validator: MacroFlowBuilderStatementValidator,
): MacroFlowBuilderValidation {
  const validationErrors: Array<MacroFlowBuilderValidationError> = [];

  switch (data.type) {
    case CommonDecisionTreeNodeType.BRANCH_BY_CONDITION: {
      const errors = data.options.flatMap((option) => {
        const [, ...conditions] = option.conditions;
        return conditions.flatMap((condition) => validator.validate(condition).errors);
      });

      validationErrors.push(...errors);

      if (
        data.options.filter((option) => option.conditions.length > 1).length < branchByConditionMinOptions
      ) {
        validationErrors.push(MacroFlowBuilderValidationErrorsEnum.EMPTY_CONDITIONS);
      }
      break;
    }
    case CommonDecisionTreeNodeType.ACTION: {
      const actionErrors = data.actions.flatMap((action) => validator.validate(action).errors);
      validationErrors.push(...actionErrors);
      if (data.actions.length === 0) {
        validationErrors.push(MacroFlowBuilderValidationErrorsEnum.EMPTY_ACTIONS);
      }
      break;
    }
    case MacroDecisionTreeNodeType.START:
    default:
      break;
  }

  return {
    isValid: validationErrors.length === 0,
    validationErrors,
  };
}
